import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EditIcon from '../../../../../components/Icons/EditIcon';
import SquareIconButton from '../../../../../components/MUIComponents/SquareIconButton';
import { useCurrentPageUserId } from '../../../../../hooks/useCurrentPageUserId';
import ArticleLayout from '../../../../../layouts/ArticleLayout';
import { GetWorkExperienceRes } from '../../../../../interfaces/user-work-experience.interface';
import WorkExperiencesList from './components/WorkExperiencesList';
import EmptyWorkExperience from './components/EmptyWorkExperience';

interface WorkExperienceTabProps {
  workExperiences?: GetWorkExperienceRes[];
}

const WorkExperienceTab = ({ workExperiences }: WorkExperienceTabProps) => {
  const { isOwnPage } = useCurrentPageUserId();
  const navigate = useNavigate();

  // Use unified workExperiences prop from /me or /:userId.
  // If undefined or empty, we simply render the empty state UI.
  const sortedWorkExps = useMemo(() => {
    if (!workExperiences) return [];
    return [...workExperiences].sort(
      (a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
  }, [workExperiences]);

  const handleExperienceButtonClick = () => {
    navigate('/profile#work-experience');
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      rowGap={{ xs: '16px', xl: '22px' }}
    >
      {/* No internal loading state here; data loading is handled by the
          parent unified user query. */}

      <ArticleLayout
        className="work-experience-article"
        display="flex"
        flexDirection="column"
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {/* No internal loading here; parent unified user query owns loading state. */}
          <Typography
            variant="h3"
            textTransform="capitalize"
            lineHeight={'125%'}
          >
            work experience
          </Typography>

          {isOwnPage && !!sortedWorkExps?.length && (
            <SquareIconButton onClick={handleExperienceButtonClick}>
              <EditIcon />
            </SquareIconButton>
          )}
        </Stack>

        {!!sortedWorkExps?.length && (
          <WorkExperiencesList workExperiences={sortedWorkExps} />
        )}

        {!sortedWorkExps?.length && (
          <EmptyWorkExperience
            isOwnPage={isOwnPage}
            handleAddWorkExperience={handleExperienceButtonClick}
            sx={{
              minHeight: { xs: 'auto', md: '354px' },
              mt: { xs: '2rem', md: '0' },
            }}
          />
        )}
      </ArticleLayout>
    </Box>
  );
};

export default WorkExperienceTab;
